import React, { useContext, useRef, useEffect } from 'react';
import { getChildValueAsString, getPropertyDisplayLabel } from 'components/property-editors/utils/ProviderNodeUtils';
import { IEditorProps } from './Editor';
import { EditorConfigButton } from './EditorConfigButton';
import { EditorConfigButtons } from './EditorConfigButtons';
import './EditorMergeString.less';
import { useEditorId } from './hooks/useEditorId';
import { getEditorDataAttributes } from './utils/EditorDataAttributeHelpers';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';
import { submitMergeTextEditorValue } from '@decisions/server/DecisionsFramework/Design/Properties/PropertyEditorService';
import { AwaitableContext } from 'contexts/AwaitableContext';
import { getMergedTextDisplayValue } from 'components/property-editors/editors/EditorStringValueUtils';

interface IEditorMergeStringProps extends IEditorProps {}

/**
 * Merge String Editor
 *
 * Copyright (c) 2019 Decisions ALl Rights Reserved
 */
export function EditorMergeString({ child, path, onChange }: IEditorMergeStringProps) {
  const hiddenFieldRef = useRef(document.createElement('textarea'));
  const valueRef = useRef('');
  const [id] = useEditorId(path);
  const dataAttrs = getEditorDataAttributes(child, path);
  const label = getPropertyDisplayLabel(child);
  const { editingContextId, onShowDialogue, refreshContext, mappingContext } = useContext(PropertyEditorContext);
  const text = getChildValueAsString(child);
  const { addPromise, removePromise } = useContext(AwaitableContext);

  useEffect(() => {
    if (!hiddenFieldRef.current) {
      valueRef.current = '';
      return;
    }
    hiddenFieldRef.current.innerHTML = text;
    valueRef.current = text;
  }, [text]);

  const showEditor = (e: React.MouseEvent<HTMLButtonElement>) => {
    onShowDialogue({
      node: child,
      path,
      editingContextId: editingContextId,
      options: {
        allowFormating: getEditorConfigValue(child, 'allowFormatting'),
        componentId: getEditorConfigValue(child, 'componentId'),
        formSessionInfoId: getEditorConfigValue(child, 'formSessionInfoId'),
        fields: getEditorConfigValue(child, 'fields'),
        reportContextId: getEditorConfigValue(child, 'ReportContextId'),
        mappingContext,
        onSave: async (value: string) => {
          const id = path.join(',');
          addPromise(
            id,
            (async () => {
              await submitText(editingContextId, value, path);
              removePromise(id);
              refreshContext(); // should this be triggering a full refresh?
            })()
          );
        },
        value: valueRef.current, // unescape(text), didn't quite do it for us here...
        buttonElement: e.currentTarget,
      },
      onDone: () => {},
    });
  };

  return (
    <div className="dpc-editor-merge-string" data-testid={id} {...dataAttrs}>
      <label className="dpc-editor-merge-string__label">{`${label}`}</label>
      <label className="dpc-editor-merge-string__content">
        {getMergedTextDisplayValue(getChildValueAsString(child))}
      </label>
      <EditorConfigButtons>
        <EditorConfigButton onClick={showEditor} className="dpc-editor-merge-string__button">
          Show Editor
        </EditorConfigButton>
      </EditorConfigButtons>
    </div>
  );
}

async function submitText(editingContextId: string, selectedvalue: string, path: string[]) {
  try {
    return await submitMergeTextEditorValue({ editingContextId, path, selectedvalue });
  } catch (error) {
    console.error('Error saving merge text data', path);
  }
}
